import { Answers, QuestionCodes } from "../../UserJourney/UserJourneyMap";
import CoreUtils from "../../core/coreUtils";

// WHO-5 Wellbeing Index (World Health Organization, 1998)
// Reference: https://www.psykiatri-regionh.dk/who-5/who-5-questionnaires/Pages/default.aspx
//
// Scoring:
// - 5 questions, each scored 0-5 (0 = "At no time", 5 = "All of the time")
// - Raw score range: 0-25, scaled to 0-100 (raw × 4)
//
// Interpretation (based on SiSU Health Check Ratings v0.6):
// - Score 0-28: Low wellbeing - may need GP/health professional support
// - Score 29-49: Reduced wellbeing - may benefit from additional support
// - Score 50-100: Generally positive wellbeing
//
// Boundaries follow Domenech (2025): score <=28 = moderate-to-severe depressive
// symptoms (low wellbeing), 29-50 = mild depressive symptoms (reduced wellbeing).
// Scores are inclusive, e.g. 28 falls into "low".

// Explicit WHO-5 scoring (0 = worst wellbeing, 5 = best wellbeing)
const AnswerScoreMap = [
    { answer: Answers.wellbeingWHO5.noneOfTheTime, score: 0 },
    { answer: Answers.wellbeingWHO5.someOfTheTime, score: 1 },
    { answer: Answers.wellbeingWHO5.lessThanHalfTheTime, score: 2 },
    { answer: Answers.wellbeingWHO5.moreThanHalfTheTime, score: 3 },
    { answer: Answers.wellbeingWHO5.mostOfTheTime, score: 4 },
    { answer: Answers.wellbeingWHO5.allTheTime, score: 5 }
];

const ScoreMap = {
    [QuestionCodes.wellbeingWHO5Cheerful]: AnswerScoreMap,
    [QuestionCodes.wellbeingWHO5CalmRelaxed]: AnswerScoreMap,
    [QuestionCodes.wellbeingWHO5ActiveVigorous]: AnswerScoreMap,
    [QuestionCodes.wellbeingWHO5FreshRested]: AnswerScoreMap,
    [QuestionCodes.wellbeingWHO5DailyLife]: AnswerScoreMap
};

// Total risk levels correspond to three bands (high risk → low risk)
const totalRiskLevel = 3;

// WHO-5 guideline definition (SiSU Health Check Ratings v0.6)
// NOTE: "high" = high RISK (low wellbeing), "low" = low RISK (good wellbeing)
// IMPORTANT: Labels are i18n keys following standard ranges.<key> pattern
const SisuWHO5GuidelineDefinition = [
    {
        min: 0,
        max: 28,
        key: "high",
        label: "ranges.who5LowWellbeing",
        rangeInfo: "0-28",
        riskLevel: 3 / (totalRiskLevel + 1) // highest risk: low wellbeing
    },
    {
        min: 29,
        max: 49,
        key: "moderate",
        label: "ranges.who5ReducedWellbeing",
        rangeInfo: "29-49",
        riskLevel: 2 / (totalRiskLevel + 1)
    },
    {
        min: 50,
        max: 100,
        key: "low",
        label: "ranges.who5PositiveWellbeing",
        rangeInfo: "50-100",
        riskLevel: 1 / (totalRiskLevel + 1) // lowest risk: positive wellbeing
    }
];

function CalcScore(answers) {
    return Object.keys(answers).reduce((calculatedScore, question) => {
        let score = 0;
        const scoreMap = ScoreMap[question];
        if (scoreMap) {
            const match = scoreMap.find(obj => obj.answer === answers[question]);
            score = match ? match.score : 0;
        }
        return calculatedScore + score;
    }, 0);
}

/**
 * Finds the guideline for the given score
 * @param {number} score - WHO-5 score (0-100)
 * @returns {object} Guideline definition object
 */
function SisuGuideline(score) {
    return SisuWHO5GuidelineDefinition.find(gl => CoreUtils.isWithin(score, gl.min, gl.max));
}

/**
 * Calculate WHO-5 wellbeing score and guideline from answers
 * @param {object} answers - Object with question codes as keys and answer values
 * @returns {object} { score, rangeInfo, key, ranges, label }
 */
function wellbeingWHO5(answers) {
    const rawScore = CalcScore(answers);
    const score = rawScore * 4; // scale to 0-100
    const guideline = SisuGuideline(score);
    const rangeInfo = guideline.rangeInfo;
    const key = guideline.key;
    const ranges = SisuWHO5GuidelineDefinition;
    const label = guideline.label;
    return { score, rangeInfo, key, ranges, label };
}

const RiskName = "WELLBEING_WHO5";
const RiskCode = "WELLBEING_WHO5";
const GuidelineName = "SiSU WELLBEING WHO5 Guideline";
const GuidelineCode = "SISU_WELLBEING_WHO5";

export {
    CalcScore,
    SisuGuideline,
    wellbeingWHO5,
    SisuWHO5GuidelineDefinition,
    RiskName,
    RiskCode,
    GuidelineName,
    GuidelineCode
};
